/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { message } from '@brokerbay/design-components';
import RetsOpenHouse from '@bb/domain/dist/types/RetsOpenHouse';
import { Button, Checkbox, Collapse, Tooltip } from 'components';
import React, {
  Fragment,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { createOrUpdateFromRetsOpenHouse } from '../../../../../api/RetsOpenHouseController';
import { ExtractKeysOfType } from '../../../../../utils/utilTypes';
import TriggerRetsUpdatePageContext from './TriggerRetsOpenHouseUpdatePageContext';
import { buildElement, getRowCount, getMongoUpdatesDiff } from '../utils';

const EXCLUDED_KEYS: Partial<Record<keyof RetsOpenHouse, true>> = {
  // @ts-ignore
  _id: true,
  createdAt: true,
  updatedAt: true,
};

const NON_PRIMITIVE_KEYS: Record<
  ExtractKeysOfType<RetsOpenHouse, any[] | Record<string, string>>,
  true
> = {
  properties: true,
  // @ts-ignore
  mlsType: true,
};

const OpenHouseEditor: React.FC = () => {
  const {
    retsOpenHouse,
    retsOpenHouseId,
    updates,
    setUpdates,
    shouldCreateCopy,
    setShouldCreateCopy,
    setRetsOpenHouseId,
    setRetsOpenHouse,
  } = useContext(TriggerRetsUpdatePageContext);

  /** Used to trigger a full re-render so that uncontrolled inputs update when a new open house is selected */
  const [rootKey, setRootKey] = useState(0);
  const previewUpdateRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setRootKey(prev => prev + 1);
  }, [retsOpenHouse]);

  if (!retsOpenHouse) return null;

  const submit = async () => {
    const mongoUpdates = getMongoUpdatesDiff(updates, retsOpenHouse);

    try {
      const id = await createOrUpdateFromRetsOpenHouse({
        shouldCreateCopy,
        // @ts-ignore
        retsOpenHouseId,
        updates: mongoUpdates,
      });
      setRetsOpenHouseId(id);
      setShouldCreateCopy(false);
      setRetsOpenHouse(oh => ({
        ...oh,
        ...updates,
        id,
        properties: { ...oh.properties, ...updates.properties },
      }));
      setUpdates({});
      setPreviewValue({});
      if (previewUpdateRef.current) previewUpdateRef.current.hidden = true;
      message.success('Submitted Successfully');
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to submit');
    }
  };

  const setPreviewValue = (docUpdates: Record<string, any>) => {
    if (!previewUpdateRef.current) return;
    const transformedUpdates = getMongoUpdatesDiff(docUpdates, retsOpenHouse);
    const newRowsCount = getRowCount(transformedUpdates, 5);
    const currentCount = previewUpdateRef.current.rows || 0;
    previewUpdateRef.current.rows = Math.max(currentCount, newRowsCount);
    previewUpdateRef.current.value = JSON.stringify(
      transformedUpdates,
      null,
      2,
    );
  };

  return (
    <Fragment key={rootKey}>
      <Collapse>
        <Collapse.Panel header="Open House Data" key="1">
          <Collapse>
            <Collapse.Panel header="Simple properties" key="1">
              <StyledSimplePropertiesContainer>
                {Object.entries(retsOpenHouse).map(
                  ([key, value]: [
                    keyof RetsOpenHouse,
                    RetsOpenHouse[keyof RetsOpenHouse],
                  ]) => {
                    if (
                      EXCLUDED_KEYS[key] ||
                      NON_PRIMITIVE_KEYS[key as keyof typeof NON_PRIMITIVE_KEYS]
                    )
                      return null;
                    return buildElement({
                      key,
                      value: updates[key] ?? value,
                      reactKey: key,
                      setUpdates,
                    });
                  },
                )}
              </StyledSimplePropertiesContainer>
            </Collapse.Panel>
          </Collapse>
          {Object.keys(NON_PRIMITIVE_KEYS).map((key: keyof RetsOpenHouse) =>
            buildElement({
              key,
              value: updates[key] ?? retsOpenHouse[key],
              setUpdates,
              reactKey: key,
            }),
          )}
        </Collapse.Panel>
      </Collapse>

      <div>
        <Tooltip title="Does not update in real-time">
          <StyledButton
            onClick={() => {
              if (!previewUpdateRef.current) return;

              setPreviewValue(updates);
              previewUpdateRef.current.hidden = false;
            }}
          >
            Preview Updates
          </StyledButton>
        </Tooltip>
        &nbsp;
        <StyledButton
          onClick={() => {
            if (!previewUpdateRef.current) return;
            previewUpdateRef.current.hidden = true;
          }}
        >
          Hide Updates
        </StyledButton>
      </div>

      <textarea readOnly hidden ref={previewUpdateRef} />

      <Checkbox
        checked={shouldCreateCopy}
        onChange={e => setShouldCreateCopy(e.target.checked)}
      >
        <Tooltip title="If unchecked the rets open house will be updated with the new values once submitted, otherwise a copy of the rets open house will be created with the new values">
          Create New Open House
        </Tooltip>
      </Checkbox>

      <StyledButton type="primary" onClick={submit}>
        Submit
      </StyledButton>
    </Fragment>
  );
};

export default OpenHouseEditor;

const StyledButton = styled(Button)`
  width: fit-content;
`;

const StyledSimplePropertiesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;
